import { Sanity$Section } from '@otovo/shared/types/sanityTypes';
import { stegaClean } from 'next-sanity';
import dynamic from 'next/dynamic';
import ElementPlaceholder from './ElementPlaceholder';
import BlockContentRow from './components/BlockContentRow/BlockContentRow';
import ContentGrid from './components/ContentGrid/ContentGrid';
import Feature from './components/Feature';
import LogoSection from './components/LogoSection/LogoSection';
import SignupSectionWrapper from './components/SignupSection/SignupSectionWrapper';
import { RowContext } from './types';

const HubspotFormSection = dynamic(
  () => import('./components/HubspotFormSection/HubspotFormSection'),
);

const TrustpilotSection = dynamic(() => import('./TrustpilotSection'));
const ReferralSignup = dynamic(() => import('./ReferralSignup'));
const TestimonialSection = dynamic(
  () => import('./components/TestimonialSection/TestimonialSection'),
);

const PriceRow = dynamic(
  () => import('../ProductPage/components/PriceRow/PriceRow'),
);
const AddressSearchBulletSection = dynamic(
  () => import('../ProductPage/components/AddressSearchBulletSection'),
);
const RelatedProducts = dynamic(
  () => import('../ProductPage/components/RelatedProducts'),
);

// V2 content rows
const HorizontalListOfFeatures = dynamic(
  () => import('./components/HorizontalListOfFeatures'),
);
const FeatureColumnComparison = dynamic(
  () => import('./components/FeatureColumnComparison'),
);
const PaymentComparison = dynamic(
  () => import('./components/PaymentComparison'),
);
const PackageColumnComparison = dynamic(
  () => import('./components/PackageComparison'),
);
const TestimonialCards = dynamic(() => import('./components/TestimonialCards'));
const CalloutWithInformationEntries = dynamic(
  () => import('./components/CalloutWithInformationEntries'),
);
const VerticalListOfFeatures = dynamic(
  () => import('./components/VerticalListOfFeatures'),
);
const CTAWithFullPageImage = dynamic(
  () => import('./components/CTAWithFullPageImage'),
);
const BlogWithFeaturedPost = dynamic(
  () => import('./components/BlogWithFeaturedPost'),
);
const TextImageCTABlock = dynamic(
  () => import('./components/TextImageCTABlock'),
);
const GradientHighlight = dynamic(
  () => import('./components/GradientHighlight'),
);

type ContentRowProps = {
  row: Sanity$Section;
  rowContext: RowContext;
};

function assertExhaustiveCheck(type: never) {
  throw new Error(
    `Unhandled row._type passed to ConentRow. "${type}" is not supported.`,
  );
}

const ContentRow = ({ row, rowContext }: ContentRowProps) => {
  const rowType = row._type;
  switch (rowType) {
    case 'rowWrapper':
      return <BlockContentRow content={row.rowContent} context={rowContext} />;
    case 'dynamicPlaceholder':
      return (
        <ElementPlaceholder
          placeholder={stegaClean(row.placeholder)}
          context={rowContext}
        />
      );
    case 'contentGrid':
      return (
        <ContentGrid
          heading={row.heading}
          description={row.description}
          items={row.items}
          stylingOptions={row.stylingOptions}
        />
      );
    case 'logoSection':
      return (
        <LogoSection
          heading={row.heading}
          description={row.description}
          logos={row.logos}
        />
      );
    case 'trustpilotSection':
      return (
        <TrustpilotSection
          heading={row.heading}
          description={row.description}
        />
      );
    case 'productSection':
      return <PriceRow content={row} />;
    case 'addressStepsSection':
      return <AddressSearchBulletSection content={row} context={rowContext} />;
    case 'relatedProductsSection':
      return <RelatedProducts content={row} />;
    case 'referralSignupSection':
      return <ReferralSignup content={row} />;
    case 'hubspotFormSection':
      return <HubspotFormSection form={row.formType[0]} />;
    case 'testimonialSection':
      return <TestimonialSection content={row} />;
    case 'feature':
      return <Feature content={row} />;
    case 'horizontalListOfFeatures':
      return <HorizontalListOfFeatures content={row} />;
    case 'featureColumnComparison':
      return <FeatureColumnComparison content={row} />;
    case 'paymentComparison':
      return <PaymentComparison content={row} />;
    case 'packageColumnComparison':
      return <PackageColumnComparison content={row} />;
    case 'testimonialCards':
      return <TestimonialCards content={row} />;
    case 'calloutWithInformationEntries':
      return <CalloutWithInformationEntries content={row} />;
    case 'verticalListOfFeatures':
      return <VerticalListOfFeatures content={row} />;
    case 'ctaWithFullPageImage':
      return <CTAWithFullPageImage content={row} />;
    case 'blogWithFeaturedPost':
      return <BlogWithFeaturedPost content={row} />;
    case 'textImageCTABlock':
      return <TextImageCTABlock content={row} />;
    case 'gradientHighlight':
      return <GradientHighlight content={row} />;
    case 'signupFormSection':
      return <SignupSectionWrapper content={row} />;
    default:
      assertExhaustiveCheck(rowType);
  }
};

export default ContentRow;
